import { useEffect, useMemo } from 'react';

import { useOrpUserProfiles } from '@/components/providers/OrpUserProfilesProvider';
import { useApplicantPointOfContactsQuery } from '@/components/shared/api/queries/applicant';
import { useApplicantAssignees } from '@/components/shared/api/queries/assignees';
import { useConditionsQuery } from '@/components/shared/api/queries/conditions';

import { MentionSuggestion } from './MentionTextarea.types';
import { buildMentionSuggestions } from './MentionTextarea.utils';

export function useMentionSuggestions(applicantId: string): MentionSuggestion[] {
    const {
        data: assignees,
        isError: assigneesError,
        error: assigneesErrorObj,
    } = useApplicantAssignees(applicantId);
    const { activeProfiles: orpUserProfiles } = useOrpUserProfiles();
    const {
        data: pointOfContacts,
        isError: pocsError,
        error: pocsErrorObj,
    } = useApplicantPointOfContactsQuery({ applicantId });
    const {
        data: conditions,
        isError: conditionsError,
        error: conditionsErrorObj,
    } = useConditionsQuery({
        applicantId,
        enabled: Boolean(applicantId),
    });

    // Log query failures so failed suggestion sources are visible in console/telemetry
    useEffect(() => {
        if (assigneesError) {
            console.error('Failed to load assignees for @mention suggestions:', assigneesErrorObj);
        }
    }, [assigneesError, assigneesErrorObj]);

    useEffect(() => {
        if (pocsError) {
            console.error(
                'Failed to load points of contact for @mention suggestions:',
                pocsErrorObj,
            );
        }
    }, [pocsError, pocsErrorObj]);

    useEffect(() => {
        if (conditionsError) {
            console.error(
                'Failed to load conditions for @mention suggestions:',
                conditionsErrorObj,
            );
        }
    }, [conditionsError, conditionsErrorObj]);

    return useMemo(
        () =>
            buildMentionSuggestions(
                assignees ?? [],
                orpUserProfiles,
                pointOfContacts ?? [],
                conditions ?? [],
            ),
        [assignees, orpUserProfiles, pointOfContacts, conditions],
    );
}
